import { useQueryClient, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";
import { NotificationCategory } from "hooks/queries/notification";

const useUpdateNotificationById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      return client.put(`/v1/notification/${notificationId}`);
    },
    onSuccess: ({ data: response }) => {
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`notification`] });
      queryClient.invalidateQueries({ queryKey: [`notifications`] });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Marks unseen notifications seen. Scoped to the category the caller was
// showing (the drawer's active filter pill), so closing a drawer filtered to
// Booking never marks the clash digests or package alerts under the other
// pills as seen when nobody looked at them. "all" (or no category) keeps the
// business-wide behaviour.
const useUpdateAllNotificationsByBusinessIdToSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (category?: NotificationCategory) => {
      return client.put(`/v1/notification`, undefined, {
        params: category && category !== "all" ? { category } : undefined,
      });
    },
    onSuccess: ({ data: response }) => {
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`notification`] });
      queryClient.invalidateQueries({ queryKey: [`notifications`] });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Marks the given rows seen (one PUT per id, in parallel; the existing
// single-row endpoint is the only per-id write there is). Refreshes ONLY the
// unseen-count badge, not the feed: the dashboard calls this for the rows it
// has just shown, and their "new" accent should survive the current visit
// rather than blink off the instant the page paints. The feed catches up on
// its next fetch (focus, navigation, or the drawer opening).
const useMarkNotificationsSeen = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      if (notificationIds.length === 0) return [];
      return Promise.all(
        notificationIds.map((id) => client.put(`/v1/notification/${id}`)),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`notification`] });
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export {
  useUpdateNotificationById,
  useUpdateAllNotificationsByBusinessIdToSeen,
  useMarkNotificationsSeen,
};
